import { NOTIFICATIONS_SERVICE_WORKER_URL } from './constants';
import { debug } from '../../utils/DebugUtil';
import defer from 'hs-promise-utils/defer';

/**
 * Gets the service worker associated with the provided registration. The service worker reference can
 * change as it goes through different lifecycle states, this utility ensures you have the live instance
 */
const getWorkerFromRegistration = registration => {
  return registration.installing || registration.waiting || registration.active;
};
const tryToRegister = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.register(NOTIFICATIONS_SERVICE_WORKER_URL, {
        scope: '/notifications/'
      });
      return registration;
    }
    return null;
  } catch (error) {
    if (error instanceof DOMException) {
      if (error.name === 'AbortError' || error.name === 'InvalidStateError') {
        debug(`failed to register notifications service worker: ${error.name}`, error);
        return null;
      }
    }
    throw error;
  }
};

/**
 * Will load the notifications service worker and wait for it to become active
 * Returns a promise that resolves with the service worker registration when active state is reached
 */
export const startServiceWorker = async () => {
  debug('starting service worker');
  const registration = await tryToRegister();
  if (registration === null) {
    debug('Not proceeding as registration failed');
    return null;
  }
  debug('Notifications service worker registered');
  const initialWorker = getWorkerFromRegistration(registration);
  if (initialWorker) {
    if (initialWorker.state === 'activated') {
      debug('service worker was immediately active');
    } else {
      debug('service worker is not ready, listening for active state');
      const deferredActivated = defer();
      const onStateChange = () => {
        const worker = getWorkerFromRegistration(registration);
        if (worker && worker.state === 'activated') {
          debug('service worker is now active');
          deferredActivated.resolve();
        }
      };
      initialWorker.addEventListener('statechange', onStateChange);
      await deferredActivated.promise;
      initialWorker.removeEventListener('statechange', onStateChange);
    }
    return registration;
  }
  debug('not starting service worker, it is unsupported');
  return null;
};